import { PropsWithChildren, ReactElement, useMemo, memo } from 'react';
import classnames from 'classnames';

import { ItemContent } from '@hh.ru/magritte-ui-tree-selector/ItemContent';
import TreeCollection from '@hh.ru/magritte-ui-tree-selector/collection/treeCollection';
import { AdditionalDefault, IdCollectionPredicate, TreeModel } from '@hh.ru/magritte-ui-tree-selector/collection/types';
import { RenderItem } from '@hh.ru/magritte-ui-tree-selector/types';

const ONE_BOX_MARGIN = 36;

const getMargin = (allBoxesCount: number, hasShift: boolean, hasIndent: boolean): number => {
    let margin = 0;
    if (allBoxesCount === 2) {
        margin = ONE_BOX_MARGIN * 2;
    } else if (allBoxesCount === 3) {
        margin = ONE_BOX_MARGIN * 3;
    } else {
        margin = ONE_BOX_MARGIN;
    }
    if (hasIndent) {
        return margin + ONE_BOX_MARGIN;
    }

    if (hasShift) {
        return margin - ONE_BOX_MARGIN;
    }

    return margin;
};

interface ItemProps<Additional extends AdditionalDefault> extends PropsWithChildren {
    collection: TreeCollection<Additional>;
    parentId?: TreeModel<Additional>['id'];
    item: TreeModel<Additional>;
    leavesOnly?: boolean;
    checkSelectable?: IdCollectionPredicate;
    isExpanded: boolean;
    isSelected: boolean;
    isDisabled: boolean;
    singleChoice?: boolean;
    isIndeterminate: boolean;
    isSearch: boolean;
    onExpansion: (id: string) => void;
    onChange: (id: string, isSelected: boolean) => void;
    renderItemForDesktop?: RenderItem<Additional>;
    cache: Record<string, number>;
}

const ItemComponent = <Additional extends AdditionalDefault>({
    collection,
    item,
    parentId,
    isExpanded,
    isSelected,
    isIndeterminate,
    isDisabled,
    leavesOnly,
    singleChoice,
    isSearch,
    checkSelectable,
    onExpansion,
    onChange,
    renderItemForDesktop,
    cache,
    children,
}: ItemProps<Additional>): ReactElement => {
    const modelsOnLevel = useMemo(
        () => (parentId ? collection.getChildren(parentId) : collection.getTopLevel()),
        [parentId, collection]
    );

    const hasLetterOnLevel = useMemo(() => {
        return !isSearch && modelsOnLevel.some((model) => model.additional?.char);
    }, [modelsOnLevel, isSearch]);

    // максимальное кол-во контролов на уровне, максимум может быть 2 - шеврон + чекбокс/радио
    const maxControlsOnLevel = useMemo(() => {
        return modelsOnLevel.reduce((currentMax, model) => {
            const hasChildren = collection.hasChildren(model.id);
            const hasAction = !(leavesOnly && hasChildren) && checkSelectable?.(model.id, collection);
            const actionCount = +hasChildren + +!!hasAction;
            return Math.max(currentMax, actionCount);
        }, 0);
    }, [modelsOnLevel, checkSelectable, leavesOnly, collection]);

    const hasChildren = collection.hasChildren(item.id);
    const hasAction = !(leavesOnly && hasChildren) && checkSelectable?.(item.id, collection);
    const hasLetter = item.additional?.char && !isSearch;
    const realControlsCount = +hasChildren + +!!hasAction;

    // точка заменяет недостающий контрол, если их суммарное кол-во на уровне больше суммарного значения на текущем уровне
    const hasDot = realControlsCount < maxControlsOnLevel && (hasAction || hasChildren);
    const hasParentAction = parentId ? !leavesOnly && checkSelectable?.(parentId, collection) : false;

    const allBoxesCount = +hasLetterOnLevel + maxControlsOnLevel;

    const hasShift =
        allBoxesCount > 1 &&
        parentId &&
        ((hasParentAction && (hasLetterOnLevel || hasChildren || hasDot)) || (!hasParentAction && hasLetterOnLevel));

    const hasIndent = allBoxesCount === 0;

    useMemo(() => {
        cache[item.id] = getMargin(allBoxesCount, !!hasShift, hasIndent) + (parentId ? cache[parentId] : 0);
        // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    }, [item.id, parentId]);

    let margin = parentId ? cache[parentId] : 0;

    margin = margin + (hasShift ? -ONE_BOX_MARGIN : 0) + (hasIndent ? ONE_BOX_MARGIN : 0);

    return (
        <div
            style={{ paddingLeft: margin }}
            data-qa={classnames('tree-selector-item', `tree-selector-item-${item.id}`, {
                'tree-selector-item-expanded': isExpanded,
                [`tree-selector-child-${parentId}`]: parentId,
            })}
        >
            <ItemContent
                item={item}
                parentId={parentId}
                hasChildren={hasChildren}
                hasAction={!!hasAction}
                letter={hasLetter ? item.additional?.char : undefined}
                isExpanded={isExpanded}
                onExpansion={onExpansion}
                isSelected={isSelected}
                onChange={onChange}
                singleChoice={singleChoice}
                isIndeterminate={isIndeterminate}
                isDisabled={isDisabled}
                hasLetterOnLevel={hasLetterOnLevel}
                hasDot={hasDot}
                maxControlsOnLevel={maxControlsOnLevel}
                isSearch={isSearch}
                renderItemForDesktop={renderItemForDesktop}
            />

            {children}
        </div>
    );
};

export const Item = memo(ItemComponent) as <Additional extends AdditionalDefault>(
    props: ItemProps<Additional>
) => ReactElement;
